import {
  type GetReceiptsApiResponse,
  type Receipt,
  type ReceiptParcel,
} from './aliases';
import {
  type GetReceiptsResponse,
  type ListReceipt,
  type PaymentProvider,
  type ShippingAddress,
  ShippingStatus,
} from './types';

import { type CurrencyCode } from '@/modules/currencies/constants';
import {
  DepopShippingStatus,
  type ListParcel,
  ManualShippingStatus,
} from '@/modules/parcels/types';

export function isManualParcel(parcel: ReceiptParcel): boolean {
  return parcel.shipping_provider?.toUpperCase() === 'MANUAL';
}

/** Upstream (UPPER_SNAKE) → web DepopShippingStatus. Depop has no `shipped` (uses `inTransit`). */
const DEPOP_STATUS: Record<string, DepopShippingStatus> = {
  AWAITING_SHIPPING: DepopShippingStatus.AwaitingShipping,
  IN_TRANSIT: DepopShippingStatus.InTransit,
  SHIPPED: DepopShippingStatus.InTransit,
  DELIVERED: DepopShippingStatus.Delivered,
  FAILED: DepopShippingStatus.Failed,
  RETURNED: DepopShippingStatus.Returned,
  SHIPPING_LABEL_EXPIRED: DepopShippingStatus.ShippingLabelExpired,
  UNKNOWN: DepopShippingStatus.Unknown,
};

/** Web ManualShippingStatus only has awaiting/shipped/unknown — collapse dispatched states to shipped. */
const MANUAL_STATUS: Record<string, ManualShippingStatus> = {
  AWAITING_SHIPPING: ManualShippingStatus.AwaitingShipping,
  UNKNOWN: ManualShippingStatus.Unknown,
};

export function mapDepopStatus(status: string): DepopShippingStatus {
  return DEPOP_STATUS[status?.toUpperCase()] ?? DepopShippingStatus.Unknown;
}

export function mapManualStatus(status: string): ManualShippingStatus {
  return MANUAL_STATUS[status?.toUpperCase()] ?? ManualShippingStatus.Shipped;
}

/**
 * The legacy web-api `shippingLabelExpired` display state (`formatParcels.ts`): a Depop-shipping
 * label counts as expired once its expiry has passed while the parcel is still awaiting shipping.
 * Mobile has no such state, so the presentation contract serves the raw upstream state plus the
 * (seller-only) label expiry and leaves the derivation to the client. Both the list and the detail
 * adapter route through here so the two views can't drift — the download-label affordance is gated
 * on this in the list (`canDownloadDepopShippingLabel`, `isEligibleForDownload`) and in the detail
 * (`useDepopShippingStatus`). Buyers never get the expiry, so this is always false for them.
 */
export function isShippingLabelExpired(
  parcelState: string | undefined,
  labelExpiresAt: string | undefined
): boolean {
  if (!labelExpiresAt) {
    return false;
  }
  return (
    parcelState?.toUpperCase() === 'AWAITING_SHIPPING' &&
    Date.parse(labelExpiresAt) < Date.now()
  );
}

function mapShippingStatus(status: Receipt['status']): ShippingStatus {
  return status === 'shipping_pending'
    ? ShippingStatus.AwaitingShipping
    : ShippingStatus.Shipped;
}

/** presentation snake_case parcel → the web `ListParcel.AnyParcel` (camelCase, discriminated). */
export function mapPresentationParcel(
  parcel: ReceiptParcel
): ListParcel.AnyParcel {
  const base = {
    parcelId: parcel.parcel_id,
    shippingProvider: parcel.shipping_provider,
    ...(parcel.purchase_line_items
      ? { purchaseLineItems: parcel.purchase_line_items }
      : {}),
    ...(parcel.shipping_deadline
      ? { shippingDeadline: parcel.shipping_deadline }
      : {}),
  };

  if (isManualParcel(parcel)) {
    return {
      ...base,
      parcelStatus: mapManualStatus(parcel.parcel_status),
      ...(parcel.shipped_at ? { shippedAt: parcel.shipped_at } : {}),
      ...(parcel.shipping_tracking_number
        ? { shippingTrackingNumber: parcel.shipping_tracking_number }
        : {}),
      ...(parcel.shipping_carrier_name
        ? { shippingCarrierName: parcel.shipping_carrier_name }
        : {}),
    };
  }

  return {
    ...base,
    parcelStatus: isShippingLabelExpired(
      parcel.parcel_status,
      parcel.label_expires_at
    )
      ? DepopShippingStatus.ShippingLabelExpired
      : mapDepopStatus(parcel.parcel_status),
    providerName: parcel.provider_name ?? '',
    ...(parcel.tracking?.reference
      ? {
          tracking: {
            reference: parcel.tracking.reference,
            url: parcel.tracking.url ?? '',
          },
        }
      : {}),
    ...(parcel.shipment_timestamp
      ? { shipmentTimestamp: parcel.shipment_timestamp }
      : {}),
  };
}

export function mapPresentationAddress(
  address: Receipt['shipping_address']
): ShippingAddress | undefined {
  if (!address) {
    return undefined;
  }
  return {
    name: address.name,
    address: address.address,
    ...(address.address2 ? { address2: address.address2 } : {}),
    city: address.city,
    ...(address.state ? { state: address.state } : {}),
    postcode: address.postal_code,
    country: address.country,
  };
}

export function mapPresentationReceipt(receipt: Receipt): ListReceipt.Receipt {
  return {
    purchaseId: receipt.purchase_id,
    transactionId: receipt.transaction_id,
    buyer: { id: receipt.buyer.id, username: receipt.buyer.username },
    seller: { id: receipt.seller.id, username: receipt.seller.username },
    shippingStatus: mapShippingStatus(receipt.status),
    soldTimestamp: receipt.sold_timestamp,
    buyerPaidAmount: receipt.buyer_amount,
    buyerPaymentProvider: receipt.buyer_payment_provider as PaymentProvider,
    buyerRefundAmount: receipt.buyer_refund_amount,
    sellerPaidAmount: receipt.seller_amount,
    currency: receipt.currency as CurrencyCode,
    hasBoostingFee: receipt.has_boosting_fee,
    isBundle: receipt.is_bundle,
    items: receipt.line_items.map((line) => ({
      pictureUrl: line.picture_url ?? '',
      productId: line.product_id,
      description: line.description,
    })),
    lineItems: receipt.line_items.map((line) => ({
      id: line.id,
      repopAvailable: line.repop_available,
    })),
    shipFromAddressId: receipt.ship_from_address_id,
    ...(receipt.status === 'cancelled' ? { isCancelled: true } : {}),
    cancellationDetails: receipt.cancellation_details,
    shippingAddress: mapPresentationAddress(receipt.shipping_address),
    parcels: (receipt.parcels ?? []).map(mapPresentationParcel),
  };
}

export function mapPresentationResponse(
  response: GetReceiptsApiResponse
): GetReceiptsResponse {
  const { has_more: hasMore, last } = response.page_info;
  const cursor = hasMore ? (last ?? null) : null;
  return {
    meta: {
      cursor,
      end: !hasMore || !cursor,
    },
    receipts: response.objects.map(mapPresentationReceipt),
  };
}
